import { useEffect, useMemo, useState } from "react";
import { fetchProductDetail, fetchProductIds } from "../api/catalogClient";
import type { ConfiguratorViewerHandle } from "../../../components/ConfiguratorViewer";
import type { BreakdownLineDto, PriceBreakdownDto } from "../api/priceClient";
import type {
  SceneInstance,
  SceneProductDetailResponse,
  SceneProductDto,
} from "../types";

interface ProductIdResult {
  productId: string | null;
  error: string | null;
}

export interface CartModalProps {
  open: boolean;
  onClose: () => void;
  /** All placed instances — the modal groups by configuration. */
  instances: SceneInstance[];
  /** Catalog product map, for image + name. */
  productById: Map<string, SceneProductDto>;
  baseUrl?: string;
  /** Short language code (e.g. "en", "dk") for the product-detail warm-up fetch,
   *  so material/surface names in the cart match the rest of the UI. Unknown/blank
   *  falls back to primary server-side. */
  lang?: string;
  /** Shared product-detail cache (surfaces + material options). Modal warms it
   *  lazily for any product not already in it so the ProductID resolver has
   *  surface metadata to work with. */
  detailCache: Map<string, SceneProductDetailResponse>;
  onCacheDetail: (detail: SceneProductDetailResponse) => void;

  /** Cart-items block authoring (from the modal:cart slot). All optional —
   *  defaults preserve a reasonable look when no cart-items block is placed. */
  description1Key?: string;
  description2Key?: string;
  projectLabel?: string;
  articleNumberLabel?: string;
  /** Column header for the price column (cart-items block authoring). */
  priceLabel?: string;
  /** Live price breakdown from the host's price source. Null/absent → no
   *  price column at all (hosts without pricing look exactly as before).
   *  Lines match rows by resolved ProductID. */
  breakdown?: PriceBreakdownDto | null;
  /** Headline shown beneath the eyebrow. The host passes the active series
   *  label so each Designer's cart reads as the series' inventory rather
   *  than a generic title. Falls back to "Element list" when absent. */
  headlineTitle?: string;
  /** Viewer handle for the modal-open dimension fetch. The footer's TOTAL
   *  MEASUREMENT cell calls viewer.callUnity("GetSceneDimensions") each
   *  time the modal opens; renders "—" while waiting, on error, or when
   *  the viewer hasn't been threaded through. */
  viewerRef?: React.RefObject<ConfiguratorViewerHandle>;
  /** Slot-rendered footer actions (PDF download, email, anything else the
   *  operator drops into the modal:cart-footer slot). The host renders the
   *  Slot and hands the result here so the cart layout stays in this file. */
  actions?: React.ReactNode;
  /** Slot-rendered totals (the modal:cart-totals slot — stat blocks for the
   *  element count + scene measurement, plus any text). When present it replaces
   *  the built-in hardcoded totals row. Absent → nothing (no legacy fallback;
   *  the default mapping always seeds this slot). */
  totals?: React.ReactNode;
  /** Slot-rendered body — the modal:cart slot dispatched through <Slot>. When
   *  present (the operator placed a composable product-table block), it replaces
   *  the built-in fixed table + header chrome below, so the whole cart interior
   *  is operator-authored. Absent → the legacy fixed table renders (back-compat
   *  for layouts that still use a metadata-only cart-items block). */
  body?: React.ReactNode;
}

interface CartRow {
  groupKey: string;
  product: SceneProductDto | null;
  productId: string;
  materialPicks: Record<string, string>;
  qty: number;
}

/**
 * Element-list modal. Groups placed instances by configuration (productId +
 * material picks); two instances with identical config show as one row, qty 2.
 *
 * Layout: project-inventory eyebrow + headline header → table of rows
 * (illustration | name + two description lines | article number | qty) →
 * footer with totals + a CLOSE button + any operator-authored action blocks
 * (PDF export, email, etc.) the cart slot mapping declares.
 */
export function CartModal({
  open,
  onClose,
  instances,
  productById,
  baseUrl,
  lang,
  detailCache,
  onCacheDetail,
  description1Key,
  description2Key,
  projectLabel,
  articleNumberLabel,
  priceLabel,
  breakdown,
  headlineTitle,
  actions,
  totals,
  body,
}: CartModalProps) {
  // Totals (element count + scene measurement) now render from the
  // modal:cart-totals slot's stat blocks, which read host state — so the cart no
  // longer fetches dimensions itself.
  // Warm-pass: fetch details for any productId we have placed but no cached
  // surface data for. Only fires while the modal is open. Needed so the SKU
  // resolver has surface metadata.
  const [warmTick, setWarmTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const need = new Set<string>();
    for (const i of instances) {
      if (!detailCache.has(i.productId)) need.add(i.productId);
    }
    if (need.size === 0) return;

    const ctrl = new AbortController();
    let cancelled = false;
    (async () => {
      await Promise.all(
        Array.from(need).map(async productId => {
          try {
            const d = await fetchProductDetail({ baseUrl, productId, lang, signal: ctrl.signal });
            if (cancelled) return;
            onCacheDetail(d);
          } catch {
            /* swallow */
          }
        })
      );
      if (!cancelled) setWarmTick(t => t + 1);
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // detailCache / onCacheDetail are stable refs from the parent. `lang` is in
    // deps so a language switch re-warms the cache with translated names.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instances, baseUrl, lang]);

  // Group instances by (productId + canonical material-picks string).
  const rows: CartRow[] = useMemo(() => {
    const map = new Map<string, CartRow>();
    for (const i of instances) {
      const key = buildGroupKey(i.productId, i.materialPicks, i.variantKey);
      const existing = map.get(key);
      if (existing) {
        existing.qty += 1;
      } else {
        map.set(key, {
          groupKey: key,
          product: productById.get(i.productId) ?? null,
          productId: i.productId,
          materialPicks: i.materialPicks,
          qty: 1,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const an = a.product?.name ?? "";
      const bn = b.product?.name ?? "";
      return an.localeCompare(bn);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, productById, warmTick]);

  // SKU lookup per row — the existing endpoint honours material picks +
  // manual SKU overrides so we can't compute it client-side.
  const [productIds, setProductIds] = useState<Map<string, ProductIdResult>>(new Map());
  const rowsSig = useMemo(
    () => rows.map(r => r.groupKey).sort().join("|"),
    [rows]
  );
  useEffect(() => {
    if (!open || rows.length === 0) {
      setProductIds(new Map());
      return;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchProductIds({
          baseUrl,
          body: {
            items: rows.map(r => ({
              groupKey: r.groupKey,
              productId: r.productId,
              materialPicks: r.materialPicks,
            })),
          },
          signal: ctrl.signal,
        });
        if (cancelled) return;
        const next = new Map<string, ProductIdResult>();
        for (const r of resp.results) {
          next.set(r.groupKey, { productId: r.productId, error: r.error });
        }
        setProductIds(next);
      } catch {
        if (!cancelled) setProductIds(new Map());
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowsSig, baseUrl]);

  if (!open) return null;

  const headerEyebrow = projectLabel?.trim() || "Project inventory";
  const articleHeader = articleNumberLabel?.trim() || "Article number";
  const titleText = headlineTitle?.trim() || "Element list";
  const priceHeader = priceLabel?.trim() || "Price";
  const showPrices = breakdown != null;
  const withPrice = showPrices ? " sd-cart--with-price" : "";

  // Match a cart row to its breakdown line via the resolved ProductID (the
  // same key the price request was built from).
  const lineFor = (groupKey: string): BreakdownLineDto | null => {
    if (!breakdown) return null;
    const sku = productIds.get(groupKey)?.productId;
    if (!sku) return null;
    return breakdown.lines.find(l => l.productId === sku) ?? null;
  };

  return (
    <div className="sd-cart-backdrop" onClick={onClose}>
      <div className="sd-cart" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        {body != null ? (
          // Slot-driven interior — the operator authored the cart body
          // (header + composable product-table) in the modal:cart slot. The
          // close button stays here so the chrome is consistent regardless of
          // what the operator dropped in.
          <>
            <button type="button" className="sd-cart__close sd-cart__close--floating"
                    onClick={onClose} aria-label="Close">
              ×
            </button>
            <div className="sd-cart__slot-body">{body}</div>
          </>
        ) : (
          <>
            <header className="sd-cart__header">
              <div className="sd-cart__header-main">
                <span className="sd-cart__eyebrow">{headerEyebrow}</span>
                <h2 className="sd-cart__title">{titleText}</h2>
              </div>
              <button type="button" className="sd-cart__close" onClick={onClose} aria-label="Close">
                ×
              </button>
            </header>

            <div className={"sd-cart__table-head" + withPrice}>
              <span className="sd-cart__col sd-cart__col--img">Illustration</span>
              <span className="sd-cart__col sd-cart__col--desc">Product description</span>
              <span className="sd-cart__col sd-cart__col--sku">{articleHeader}</span>
              {showPrices && <span className="sd-cart__col sd-cart__col--price">{priceHeader}</span>}
              <span className="sd-cart__col sd-cart__col--qty">Quantity</span>
            </div>

            <div className="sd-cart__body">
              {rows.length === 0 ? (
                <p className="sd-cart__empty">Drag a product from the catalog to add it here.</p>
              ) : (
                <ul className="sd-cart__list">
                  {rows.map((r, idx) => (
                    <CartRowView
                      key={r.groupKey}
                      row={r}
                      zebra={idx % 2 === 1}
                      description1Key={description1Key}
                      description2Key={description2Key}
                      productIdResult={productIds.get(r.groupKey) ?? null}
                      priceLine={showPrices ? lineFor(r.groupKey) : undefined}
                    />
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <footer className="sd-cart__footer">
          {/* Totals — the modal:cart-totals slot (stat blocks: element count +
              scene measurement, each with a translatable label + unit). Replaces
              the old hardcoded "Total units / Total measurement" composite. */}
          {totals && <div className="sd-cart__totals">{totals}</div>}
          {/* Operator-authored action blocks (modal:cart-footer slot) render
              here — including the Close button, which is no longer hardcoded.
              The default footer mapping seeds a close-cart action block (its
              verb calls host.closeTopModal, closing the cart it sits in) plus
              the PDF download / email buttons. Operators can reorder, restyle,
              relabel, or remove any of them. */}
          <div className="sd-cart__actions">
            {actions}
          </div>
        </footer>
      </div>
    </div>
  );
}

function CartRowView({
  row,
  zebra,
  description1Key,
  description2Key,
  productIdResult,
  priceLine,
}: {
  row: CartRow;
  zebra: boolean;
  description1Key?: string;
  description2Key?: string;
  productIdResult: ProductIdResult | null;
  /** undefined → no price column rendered; null → column shown, line unmatched. */
  priceLine?: BreakdownLineDto | null;
}) {
  const productName = row.product?.name ?? "Product";
  const values = row.product?.parameterValues ?? null;
  const desc1 = description1Key && values ? values[description1Key] : undefined;
  const desc2 = description2Key && values ? values[description2Key] : undefined;

  const withPrice = priceLine !== undefined ? " sd-cart--with-price" : "";

  return (
    <li className={"sd-cart__row" + (zebra ? " sd-cart__row--zebra" : "") + withPrice}>
      <div className="sd-cart__row-img">
        {row.product?.imageUrl ? (
          <img src={row.product.imageUrl} alt={productName} draggable={false} />
        ) : (
          <span className="sd-cart__row-placeholder" aria-hidden="true" />
        )}
      </div>
      <div className="sd-cart__row-info">
        <div className="sd-cart__row-name">{productName}</div>
        {desc1 && <div className="sd-cart__row-desc">{desc1}</div>}
        {desc2 && <div className="sd-cart__row-desc">{desc2}</div>}
      </div>
      <div className="sd-cart__row-sku">
        {productIdResult?.productId ? (
          <span title={productIdResult.productId}>{productIdResult.productId}</span>
        ) : productIdResult?.error ? (
          <span className="sd-cart__row-sku--error" title={productIdResult.error}>—</span>
        ) : (
          <span className="sd-cart__row-sku--muted">…</span>
        )}
      </div>
      {priceLine !== undefined && (
        <div className="sd-cart__row-price">
          {priceLine?.priced ? (
            <>
              <span>{priceLine.formattedLineTotal ?? String(priceLine.lineTotal)}</span>
              {priceLine.includedQuantity > 0 && (
                <span className="sd-cart__row-price-included">
                  {priceLine.includedQuantity === priceLine.quantity
                    ? "Included"
                    : `${priceLine.includedQuantity} included`}
                </span>
              )}
            </>
          ) : (
            <span className="sd-cart__row-sku--muted">—</span>
          )}
        </div>
      )}
      <div className="sd-cart__row-qty" aria-label={`Quantity ${row.qty}`}>
        {row.qty}
      </div>
    </li>
  );
}

/**
 * Deterministic grouping key. Object property iteration isn't guaranteed to
 * preserve insertion order in older runtimes — sort the keys so identical picks
 * always serialise the same way regardless of how the surface keys were added.
 */
function buildGroupKey(
  productId: string,
  picks: Record<string, string>,
  variantKey: string | undefined
): string {
  const sortedKeys = Object.keys(picks).sort();
  const serialised = sortedKeys.map(k => `${k}=${picks[k]}`).join("&");
  // "main" and undefined collapse to the same group — both are "default
  // variant", just expressed two ways depending on whether the instance has
  // been toggled and back. Anything else gets folded into the group key so
  // open vs. closed cabinets count as separate cart lines.
  const variantPart = variantKey && variantKey !== "main" ? `|v=${variantKey}` : "";
  return `${productId}|${serialised}${variantPart}`;
}
